import { useState } from 'react';
import { ClassData, UserInfo } from '../types';
import { createSignup, cancelSignup, normalizeEmail } from '../services/signupService';

interface ErrorModalState {
  isOpen: boolean;
  title: string;
  message: string;
}

export const useBooking = (
  db: any, 
  appId: string, 
  user: any, 
  userInfo: UserInfo, 
  setIsUserRecognized: (val: boolean) => void,
  isGoogleUser?: boolean
) => {
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [bookingClass, setBookingClass] = useState<ClassData | null>(null);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorModal, setErrorModal] = useState<ErrorModalState>({ isOpen: false, title: '', message: '' });

    const showError = (title: string, message: string) => {
      setErrorModal({ isOpen: true, title, message });
    };

    const closeErrorModal = () => {
      setErrorModal({ isOpen: false, title: '', message: '' });
    };

    const handleBookingStart = (cls: ClassData) => {
        setBookingClass(cls);
        setIsBookingModalOpen(true);
    };

    const handleConfirmBooking = async (paymentOption: 'venmo' | 'arrangement' | null, finalUserInfo?: UserInfo): Promise<boolean> => {
        if (!user || !bookingClass || isSubmitting) return false;
        
        setIsSubmitting(true);
        
        const rawData = finalUserInfo || userInfo;
        // Normalize email for consistent matching
        const dataToSave = {
            ...rawData,
            email: normalizeEmail(rawData.email)
        };

        // Save user info to localStorage
        localStorage.setItem('yoga_user_info', JSON.stringify(dataToSave));
        setIsUserRecognized(true);
    
        try {
          let finalStatus = 'confirmed';
          let method: 'venmo' | 'arrangement' | 'none' = 'none';
          let paymentStatusValue: 'pending' | 'completed' | 'venmo' = 'completed';
    
          if (bookingClass.paymentType === 'venmo') {
             if (paymentOption === 'arrangement') {
                finalStatus = 'arrangement_pending'; 
                method = 'arrangement';
                paymentStatusValue = 'pending';
             } else {
                finalStatus = 'paid_pending'; 
                method = 'venmo';
                // Use 'venmo' status - payment will happen after registration
                paymentStatusValue = 'venmo';
             }
          }
    
          // Use atomic transaction to prevent race conditions
          const result = await createSignup(db, appId, {
            classId: bookingClass.id,
            firstName: dataToSave.firstName,
            lastName: dataToSave.lastName,
            email: dataToSave.email,
            status: finalStatus,
            paymentMethod: method,
            paymentStatus: paymentStatusValue
          });
          
          if (result.success) {
            setIsBookingModalOpen(false);
            setIsConfirmationModalOpen(true);
            return true;
          } else {
            showError('Booking Error', result.message || 'Error booking class.');
            return false;
          }
        } catch (error) {
          console.error("Booking failed:", error);
          showError('Booking Error', 'Error booking class. Please try again.');
          return false;
        } finally {
          setIsSubmitting(false);
        }
    };

    const handleCancelClick = () => {
        setIsManageModalOpen(false);
        setIsCancelModalOpen(true);
    };

    const executeCancellation = async () => {
        if (!user || !bookingClass || isSubmitting) return;
        
        setIsSubmitting(true);
    
        try {
            // Use the correct email - prioritize Google user email, then localStorage userInfo
            const emailToUse = (isGoogleUser && user?.email) 
              ? user.email 
              : userInfo.email;
            
            const result = await cancelSignup(db, appId, bookingClass.id, emailToUse);
            
            if (result.success) {
                setIsCancelModalOpen(false);
                setBookingClass(null);
            } else {
                showError('Cancellation Error', result.message || 'Error cancelling signup.');
            }
        } catch (error) { 
            console.error("Cancel failed:", error);
            showError('Cancellation Error', 'Error cancelling. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        isBookingModalOpen,
        setIsBookingModalOpen,
        bookingClass,
        setBookingClass,
        isConfirmationModalOpen,
        setIsConfirmationModalOpen,
        isManageModalOpen,
        setIsManageModalOpen,
        handleBookingStart,
        handleConfirmBooking,
        handleCancelClick,       
        executeCancellation,
        isCancelModalOpen,
        setIsCancelModalOpen,
        isSubmitting,
        errorModal,
        closeErrorModal
    };
};
